import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../logo.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import "./ThankYouPartner.css";

const VendorThankYou = () => {
  const contentRef = useRef(null);

  // Define states here clearly
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [first_name, setfirst_name] = useState("");
  const [vendorID, setVendorID] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    setCurrentDate(formattedDate);

    // A signup that was already processed lives in "completedVendorSignup",
    // so refreshes and re-mounts show the SAME confirmation instead of
    // losing it
    const pendingString = localStorage.getItem("pendingVendorSignup");
    const completedString = localStorage.getItem("completedVendorSignup");

    if (!pendingString && !completedString) {
      setNoData(true);
      setLoading(false);
      return;
    }

    let record;

    if (pendingString) {
      record = JSON.parse(pendingString);

      // Move pending -> completed so refreshes re-show this same signup
      localStorage.setItem("completedVendorSignup", JSON.stringify(record));
      localStorage.removeItem("pendingVendorSignup");
    } else {
      // No new signup   re-show the last completed one (e.g. page refresh)
      record = JSON.parse(completedString);
    }

    setfirst_name(record.first_name || "Partner");

    // The confirmation id (e.g. GOH-20260806-F73C2B) is created when the
    // listing is submitted and emailed. The signup form must save that same
    // value into pendingVendorSignup   any of these keys will be picked up:
    setVendorID(
      record.vendorId ||
        record.confirmationId ||
        record.confirmationNumber ||
        record.confirmation ||
        ""
    );

    setLoading(false);
  }, []);

  // Copy Confirmation ID to clipboard
  const copyVendorID = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(vendorID)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  // PDF Generation
  // All style tweaks happen in html2canvas's CLONE of the page, so the
  // on-screen card never changes or flickers.
  const generatePDF = async () => {
    const input = contentRef.current;
    if (!input) return;

    // Capturing before webfonts finish loading mangles text spacing
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    const canvas = await html2canvas(input, {
      scale: 3,
      backgroundColor: "#faf8fd", // matches --hub-surface-soft
      useCORS: true,
      logging: false,
      onclone: (clonedDoc) => {
        // Hide buttons in the clone only
        clonedDoc
          .querySelectorAll(".pdf-hide")
          .forEach((el) => (el.style.display = "none"));

        const card = clonedDoc.querySelector(".thank-you-card");
        if (!card) return;

        // html2canvas mis-measures kerned/ligatured webfont text, which
        // makes words run together in the PDF   neutralize those features
        const fixText = (el) => {
          el.style.letterSpacing = "normal";
          el.style.fontKerning = "none";
          el.style.fontVariantLigatures = "none";
          // If words STILL merge with your html2canvas version, uncomment:
          // el.style.fontFamily = "Helvetica, Arial, sans-serif";
        };
        fixText(card);
        card.querySelectorAll("*").forEach(fixText);

        // Inset box-shadows render as a thick ring in html2canvas  
        // swap the gold inset bar for a real border in the capture
        const box = clonedDoc.querySelector(".vendor-id-box");
        if (box) {
          box.style.boxShadow = "none";
          box.style.borderLeft = "4px solid #f4c64f";
        }

        // Outer drop shadows just clip/artifact in the capture
        card.style.boxShadow = "none";
        const badge = clonedDoc.querySelector(".success-icon");
        if (badge) badge.style.boxShadow = "none";
      },
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Keep the card's aspect ratio instead of stretching to the full page
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    const y = imgHeight < pdfHeight ? (pdfHeight - imgHeight) / 2 : 0;
    pdf.addImage(imgData, "PNG", 0, y, pdfWidth, imgHeight);

    pdf.save(`Thank_You_${first_name}.pdf`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="thank-you-page">
        <div className="thank-you-loading" role="status">
          <div className="loading-spinner" aria-hidden="true" />
          <p>Processing your payment details, please wait…</p>
        </div>
      </div>
    );
  }

  // If no data was found, user might have navigated here directly
  if (noData) {
    return (
      <div className="thank-you-page">
        <div className="thank-you-card">
          <header className="thank-you-header">
            <div className="thank-you-brand">
              <img src={logo} alt="GoOurHub Logo" />
              <span className="thank-you-brand-name">GoOurHub</span>
            </div>
            <p className="thank-you-date">{currentDate}</p>
          </header>

          <h2 className="thank-you-title">No Signup Data Found</h2>
          <p className="thank-you-text">
            If you just completed a payment and reached this page, please{" "}
            <Link to="/contact" className="thank-you-contact">
              contact us
            </Link>{" "}
            for assistance.
          </p>

          <div className="thank-you-actions">
            <Link to="/" className="hub-home-button">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Normal "Thank You" content
  return (
    <div className="thank-you-page">
      <div ref={contentRef} className="thank-you-card">
        {/* Header: brand on the left, date pill on the right */}
        <header className="thank-you-header">
          <div className="thank-you-brand">
            <img src={logo} alt="GoOurHub Logo" />
            <span className="thank-you-brand-name">GoOurHub</span>
          </div>
          <p className="thank-you-date">{currentDate}</p>
        </header>

        {/* Success badge (purple tile with animated check + gold glow) */}
        <div className="success-icon" aria-hidden="true">
          <span className="success-icon__glow" />
          <svg viewBox="0 0 52 52">
            <path className="success-icon-check" fill="none" d="M14 27l8 8 16-17" />
          </svg>
        </div>

        <span className="thank-you-kicker">Application received</span>
        <h1 className="thank-you-title">
          Thank You, {first_name.charAt(0).toUpperCase() + first_name.slice(1)}!
        </h1>
        <p className="thank-you-subtitle">
          Your business listing has been received and is pending review.
        </p>

        {/* Confirmation ID card   shows the same number as the email */}
        {vendorID ? (
          <div className="vendor-id-box">
            <span className="vendor-id-label">Your Confirmation ID</span>
            <span className="vendor-id-value">{vendorID}</span>
            <button
              type="button"
              className="copy-button pdf-hide"
              onClick={copyVendorID}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        ) : (
          <p className="thank-you-text">
            Your confirmation number has been emailed to you.
          </p>
        )}

        <p className="thank-you-text">
          We are thrilled that you have signed up. Welcome to{" "}
          <strong>GoOurHub</strong>. We appreciate your business   together we
          will create <strong>amazing opportunities</strong>.
        </p>
        <p className="thank-you-text">
          Keep your confirmation number for your records. If you have any
          questions, feel free to{" "}
          <Link to="/contact" className="thank-you-contact">
            contact us
          </Link>
          .
        </p>

        {/* Actions (hidden in the PDF) */}
        <div className="thank-you-actions pdf-hide">
          <button className="hub-download-button" onClick={generatePDF}>
            <svg
              className="button-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v12" />
              <path d="M7 10l5 5 5-5" />
              <path d="M4 21h16" />
            </svg>
            Download as PDF
          </button>

          {/* TODO: point this to your actual vendor signup route */}
          <Link to="/vendor-signup" className="hub-submit-button">
            + Submit Another Form
          </Link>

          <Link to="/" className="hub-home-button">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VendorThankYou;
